import * as XLSX from 'xlsx';

const REPORT_TITLE =
  'BÁO CÁO QUÁ TRÌNH ĐÀO TẠO THỰC HÀNH LÁI XE TRÊN ĐƯỜNG GIAO THÔNG CỦA HỌC VIÊN';

const STUDENT_LABELS = [
  ['Họ và tên', 'fullName'],
  ['Mã học viên', 'studentCode'],
  ['Ngày sinh', 'birthday'],
  ['Mã khóa học', 'courseCode'],
  ['Hạng đào tạo', 'license'],
  ['Cơ sở đào tạo', 'school'],
];

const COURSE_TO_LICENSE = {
  BSS: 'B',
  'BTĐ': 'B.01',
  C1: 'C1',
};

export function deriveLicenseFromCourse(tenKhoaHoc) {
  if (!tenKhoaHoc) return '';
  const prefix = String(tenKhoaHoc).split('-')[0]?.trim();
  if (!prefix) return '';
  return COURSE_TO_LICENSE[prefix] ?? prefix;
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' }).replace(',', '');
}

function round2(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 100) / 100;
}

export function buildReportRows(sessions, { skipViolations = true } = {}) {
  const filtered = (sessions || []).filter((s) => {
    if (!skipViolations) return true;
    return !s?.violation || s.violation.length === 0;
  });

  // Tương thích CẢ v1 (snake_case) LẪN v2 (camelCase) — modal dùng chung.
  const rows = filtered.map((s, idx) => ({
    stt: idx + 1,
    sessionId: s.sessionId ?? s.session_id ?? '',
    date: formatDateTime(s.startTime ?? s.start_time),
    duration: round2(s.duration),
    distance: round2(s.distance),
    note: s.vehiclePlate ?? s.vehicle_plate ?? '',
  }));

  const totalDuration = round2(rows.reduce((a, r) => a + r.duration, 0));
  const totalDistance = round2(rows.reduce((a, r) => a + r.distance, 0));

  return { rows, totalDuration, totalDistance };
}

function buildPdfDocDefinition(student, sessions, opts) {
  const { rows, totalDuration, totalDistance } = buildReportRows(sessions, opts);

  const infoTable = STUDENT_LABELS.map(([label, key], i) => [
    { text: `${i + 1}. ${label}:`, bold: false, border: [false, false, false, false] },
    { text: student[key] || '', bold: true, border: [false, false, false, false] },
  ]);

  const tableBody = [
    [
      { text: 'STT', alignment: 'center', bold: true, rowSpan: 2 },
      { text: 'Mã phiên học', alignment: 'center', bold: true, rowSpan: 2 },
      { text: 'Ngày đào tạo', alignment: 'center', bold: true, rowSpan: 2 },
      { text: 'Đào tạo thực hành lái xe trên đường giao thông', alignment: 'center', bold: true, colSpan: 2 },
      {},
      { text: 'Ghi chú', alignment: 'center', bold: true, rowSpan: 2 },
    ],
    [
      {},
      {},
      {},
      { text: 'Thời gian', alignment: 'center', bold: true },
      { text: 'Quãng đường', alignment: 'center', bold: true },
      {},
    ],
    ...rows.map((r) => [
      { text: String(r.stt), alignment: 'center' },
      { text: r.sessionId, alignment: 'left' },
      { text: r.date, alignment: 'left' },
      { text: r.duration.toFixed(2), alignment: 'right' },
      { text: r.distance.toFixed(2), alignment: 'right' },
      { text: r.note, alignment: 'left' },
    ]),
    [
      { text: 'Tổng cộng', colSpan: 3, alignment: 'center', bold: true },
      {},
      {},
      { text: totalDuration.toFixed(2), alignment: 'right', bold: true },
      { text: totalDistance.toFixed(2), alignment: 'right', bold: true },
      { text: '', alignment: 'left' },
    ],
  ];

  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { fontSize: 11 },
    content: [
      { text: REPORT_TITLE, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
      { text: 'I. Thông tin học viên', bold: true, margin: [0, 0, 0, 4] },
      {
        table: {
          widths: [120, '*'],
          body: infoTable,
        },
        layout: 'noBorders',
        margin: [10, 0, 0, 8],
      },
      { text: 'II. Thông tin quá trình đào tạo:', bold: true, margin: [0, 0, 0, 6] },
      {
        table: {
          widths: [30, 160, 115, 55, 70, 70],
          headerRows: 2,
          body: tableBody,
        },
      },
      {
        columns: [
          { text: 'XÁC NHẬN CỦA HỌC VIÊN', alignment: 'center', bold: true, margin: [0, 30, 0, 0] },
          { text: 'XÁC NHẬN CỦA CƠ SỞ ĐÀO TẠO', alignment: 'center', bold: true, margin: [0, 30, 0, 0] },
        ],
      },
      {
        columns: [
          { text: '', alignment: 'center' },
          { text: student.signer || '', alignment: 'center', margin: [0, 80, 0, 0] },
        ],
      },
    ],
  };
}

async function loadPdfMake() {
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const vfsModule = await import('pdfmake/build/vfs_fonts');
  const pdfMake = pdfMakeModule.default || pdfMakeModule;
  if (!pdfMake.vfs) {
    pdfMake.vfs = vfsModule.default || vfsModule;
  }
  return pdfMake;
}

export async function exportReportPdf(student, sessions, opts = {}) {
  const pdfMake = await loadPdfMake();
  const docDef = buildPdfDocDefinition(student, sessions, opts);
  const filename = `bao-cao-${student.studentCode || 'hoc-vien'}.pdf`;
  pdfMake.createPdf(docDef).download(filename);
}

export function exportReportXlsx(student, sessions, opts = {}) {
  const { rows, totalDuration, totalDistance } = buildReportRows(sessions, opts);

  const aoa = [];
  aoa.push([REPORT_TITLE]);
  aoa.push([]);
  aoa.push(['I. Thông tin học viên']);
  STUDENT_LABELS.forEach(([label, key], i) => {
    aoa.push([`${i + 1}. ${label}:`, student[key] || '']);
  });
  aoa.push([]);
  aoa.push(['II. Thông tin quá trình đào tạo:']);

  const headerRowIdx = aoa.length;
  aoa.push(['STT', 'Mã phiên học', 'Ngày đào tạo', 'Đào tạo thực hành lái xe trên đường giao thông', '', 'Ghi chú']);
  aoa.push(['', '', '', 'Thời gian', 'Quãng đường', '']);

  rows.forEach((r) => {
    aoa.push([r.stt, r.sessionId, r.date, r.duration, r.distance, r.note]);
  });

  const totalRowIdx = aoa.length;
  aoa.push(['Tổng cộng', '', '', totalDuration, totalDistance, '']);
  aoa.push([]);
  const signRowIdx = aoa.length;
  aoa.push(['XÁC NHẬN CỦA HỌC VIÊN', '', '', 'XÁC NHẬN CỦA CƠ SỞ ĐÀO TẠO', '', '']);
  aoa.push([]);
  aoa.push([]);
  aoa.push([]);
  aoa.push(['', '', '', student.signer || '', '', '']);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 6 },
    { wch: 28 },
    { wch: 22 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
  ];

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
    { s: { r: 2 + STUDENT_LABELS.length + 2, c: 0 }, e: { r: 2 + STUDENT_LABELS.length + 2, c: 5 } },
    { s: { r: headerRowIdx, c: 0 }, e: { r: headerRowIdx + 1, c: 0 } },
    { s: { r: headerRowIdx, c: 1 }, e: { r: headerRowIdx + 1, c: 1 } },
    { s: { r: headerRowIdx, c: 2 }, e: { r: headerRowIdx + 1, c: 2 } },
    { s: { r: headerRowIdx, c: 3 }, e: { r: headerRowIdx, c: 4 } },
    { s: { r: headerRowIdx, c: 5 }, e: { r: headerRowIdx + 1, c: 5 } },
    { s: { r: totalRowIdx, c: 0 }, e: { r: totalRowIdx, c: 2 } },
    { s: { r: signRowIdx, c: 0 }, e: { r: signRowIdx, c: 2 } },
    { s: { r: signRowIdx, c: 3 }, e: { r: signRowIdx, c: 5 } },
    { s: { r: signRowIdx + 4, c: 3 }, e: { r: signRowIdx + 4, c: 5 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BaoCao');
  const filename = `bao-cao-${student.studentCode || 'hoc-vien'}.xlsx`;
  XLSX.writeFile(wb, filename);
}

export const DEFAULT_SCHOOL = 'Trung tâm Giáo dục Nghề nghiệp Phú Giáo';
export const DEFAULT_SIGNER = 'Tạ Thị Phương An';
